// packages/product-vantage/src/lib/connected-signals.ts
import type { SignalRow } from '@bloo-team/api-client'
import { pick } from './signal-model'

/**
 * Connected signals — a client port of the search-worker task
 * `dl.connectedsignals` (celery_lib/m_connected_signal_task.py), run over the
 * demo fixture so demo mode and live never disagree (docs/signals.md
 * §Storylines; the F-list: docs/signals.md, *Worker parity*).
 *
 * PARITY, FLAWS INCLUDED. Every quirk below is the worker's, reproduced on
 * purpose and pinned by a test (F-numbers in docs/signals.md): raw-value entity ids,
 * last-write field/group, Python int() weights, the strict > filter, the
 * silent >1000-link drop, the loose merge, networkx's nbunch_iter quirks.
 * ONE divergence (D1): the worker walks a component as a Python set (hash-
 * seed order); the port walks first-insertion order, deterministically.
 *
 * Pure: no React, no I/O. Output is the RESULT-ENDPOINT body, so demo goes
 * through the same zod parse as live.
 */

export const CS_ENTITY_FIELDS = [
  '$TargetHost', '$TargetUser', '$TargetResource', '$TargetPort',
  '$SuspectHost', '$SuspectUser', '$SuspectObject', '$SuspectProcess',
  '$SuspectHash', '$SuspectUrl', '$SuspectAction', '$SuspectLocation',
] as const
const CS_SIGNAL_FIELDS = [
  '$Uuid', '$DetectionScore', '$DetectionName', '$DetectionTactic', '$DetectionTechnique',
  '$DetectionSubtechnique', '$DetectionSeverity', '$DetectionConfidence', '$CNAMTime', '$SourceId', '$SourceVersion',
] as const
type WorkerKey = (typeof CS_SIGNAL_FIELDS)[number] | (typeof CS_ENTITY_FIELDS)[number]

export type CsRaw = string | number | boolean | null
export type WorkerRow = Record<WorkerKey, CsRaw>

/** What makes the worker raise — the job fails, demo shows the error state. */
export class ConnectedSignalsPortError extends Error {
  constructor(message: string) {
    super(message)
    this.name = 'ConnectedSignalsPortError'
  }
}

const toRaw = (v: unknown): CsRaw =>
  typeof v === 'string' || typeof v === 'number' || typeof v === 'boolean' ? v : null

/** A queue row (either vocabulary) -> the worker's `$`-keyed input. Absent -> null (F14). */
export function workerRow(row: SignalRow): WorkerRow {
  const out = {} as WorkerRow
  for (const k of [...CS_SIGNAL_FIELDS, ...CS_ENTITY_FIELDS]) out[k] = toRaw(pick(row, k))
  if (out.$SourceId === null) out.$SourceId = toRaw(pick(row, 'Workbook'))
  return out
}

/* ------------------------------------------------------- Python semantics */

type Id = string | number | boolean
const isNum = (x: unknown): x is number | boolean => typeof x === 'number' || typeof x === 'boolean'
/** dict-key identity: '443' and 443 differ; 1, 1.0 and True collide (F1). */
const keyOf = (v: Id): string => (typeof v === 'string' ? `s:${v}` : `n:${Number(v)}`)
const pyTruthy = (v: CsRaw): v is Id =>
  v !== null && v !== false && v !== '' && !(typeof v === 'number' && (v === 0 || Number.isNaN(v)))
function pyStr(v: unknown): string {
  if (v === null || v === undefined) return 'None'
  if (typeof v === 'boolean') return v ? 'True' : 'False'
  return String(v)
}
function pyEq(a: unknown, b: unknown): boolean {
  if (isNum(a) && isNum(b)) return Number(a) === Number(b)
  return a === b
}
/** Python int(): entity nodes have no weight (-> 0); null / '5.0' raise (F3, F4). */
function pyInt(v: CsRaw | undefined): number {
  if (v === undefined) return 0
  if (typeof v === 'boolean') return v ? 1 : 0
  if (typeof v === 'number' && Number.isFinite(v)) return Math.trunc(v)
  if (typeof v === 'string' && /^\s*[+-]?\d+(_\d+)*\s*$/.test(v)) return parseInt(v.replace(/_/g, ''), 10)
  throw new ConnectedSignalsPortError(`int() of ${pyStr(v)} — $DetectionScore must be an integer`)
}
/** Python `<` — raises on None and on str vs number (F18). */
function pyLess(a: unknown, b: unknown): boolean {
  if (typeof a === 'string' && typeof b === 'string') return a < b
  if (isNum(a) && isNum(b)) return Number(a) < Number(b)
  throw new ConnectedSignalsPortError(`'<' not supported between ${pyStr(a)} and ${pyStr(b)}`)
}
/** json.dumps dict-key conversion for connected_sig_workbooks. */
const pyJsonKey = (v: unknown): string => (v === null || v === undefined ? 'null' : typeof v === 'boolean' ? String(v) : String(v))

/* ------------------------------------------------------------------- graph */

interface GNode {
  id: Id
  attrs: Record<string, CsRaw>
  succ: string[]
  pred: string[]
}
type Graph = Map<string, GNode>

function addNode(g: Graph, key: string, id: Id, attrs: Record<string, CsRaw>) {
  const n = g.get(key)
  if (n) Object.assign(n.attrs, attrs)
  else g.set(key, { id, attrs: { ...attrs }, succ: [], pred: [] })
}
function addEdge(g: Graph, a: string, b: string) {
  const na = g.get(a)!
  if (na.succ.includes(b)) return
  na.succ.push(b)
  g.get(b)!.pred.push(a)
}
/**
 * len(G.out_edges(v)) / len(G.in_edges(v)) as networkx computes it BEFORE v
 * exists: a missing string is iterated per character (F17); a missing number
 * raises (F16).
 */
function degree(g: Graph, v: Id, dir: 'succ' | 'pred'): number {
  const n = g.get(keyOf(v))
  if (n) return n[dir].length
  if (typeof v !== 'string') throw new ConnectedSignalsPortError('nbunch is not a node or a sequence of nodes.')
  let total = 0
  for (const c of new Set(v)) total += g.get(`s:${c}`)?.[dir].length ?? 0
  return total
}

function build(rows: WorkerRow[]): Graph {
  const g: Graph = new Map()
  for (const r of rows) {
    const sig = pyStr(r.$Uuid)
    const sk = `s:${sig}`
    addNode(g, sk, sig, {
      weight: r.$DetectionScore, dname: r.$DetectionName, dtactic: r.$DetectionTactic, dtechnique: r.$DetectionTechnique,
      dsubtechnique: r.$DetectionSubtechnique, dseverity: r.$DetectionSeverity, dconfidence: r.$DetectionConfidence,
      field: 'Signal', timestamp: r.$CNAMTime, group: 'signal', sourceid: r.$SourceId, version: r.$SourceVersion,
    })
    for (const f of CS_ENTITY_FIELDS) {
      const v = r[f]
      if (!pyTruthy(v) || v === 'None') continue
      const ek = keyOf(v)
      if (f.includes('$Target')) {
        const group = degree(g, v, 'succ') > 0 ? 'compromised' : 'target'
        addNode(g, ek, v, { field: f.slice('$Target'.length), group })
        addEdge(g, sk, ek)
      } else {
        const group = degree(g, v, 'pred') > 0 ? 'compromised' : 'suspect'
        addNode(g, ek, v, { field: f.slice('$Suspect'.length), group })
        addEdge(g, ek, sk)
      }
    }
  }
  return g
}

/** nx.weakly_connected_components: discovery order; members in insertion order (D1). */
function components(g: Graph): string[][] {
  const order = new Map([...g.keys()].map((k, i) => [k, i]))
  const seen = new Set<string>()
  const out: string[][] = []
  for (const k of g.keys()) {
    if (seen.has(k)) continue
    const comp = new Set<string>([k])
    const queue = [k]
    while (queue.length) {
      const n = g.get(queue.shift()!)!
      for (const y of [...n.succ, ...n.pred]) {
        if (!comp.has(y)) {
          comp.add(y)
          queue.push(y)
        }
      }
    }
    comp.forEach((y) => seen.add(y))
    out.push([...comp].sort((a, b) => order.get(a)! - order.get(b)!))
  }
  return out
}

/* ------------------------------------------------------------- subgraphs */

type Dict = Record<string, unknown>
const COMP_KEYS = ['DetectionName', 'DetectionSeverity', 'DetectionTactic', 'DetectionTechnique', 'DetectionSubtechnique', 'group', 'weight'] as const
const SPAN = '<span className={style.GraphEntityName}>'

function distinct(xs: unknown[]): number {
  return new Set(xs.map((x) => (x === null || x === undefined ? 'none' : isNum(x) ? `n:${Number(x)}` : `s:${String(x)}`))).size
}

/** get_subgraph_name — [subgraph_name, title_html, entity_name]. */
function subgraphName(D: unknown[], S: Id[], St: unknown[], T: Id[], Tt: unknown[], C: Id[]): [string, string, string | null] {
  const dn = distinct(D)
  const f = pyStr
  if (dn > 1 && T.length === 1)
    return [`Multiple attacks targeting ${f(Tt[0])}: ${f(T[0])}`, `Multiple attacks targeting ${f(Tt[0])}: ${SPAN}${f(T[0])}</span>`, f(T[0])]
  if (dn > 1 && S.length === 1)
    return [`Multiple attacks originating from ${f(St[0])}: ${f(S[0])}`, `Multiple attacks originating from ${f(St[0])}: ${SPAN}${f(S[0])}</span>`, f(S[0])]
  if (S.length > 2 && T.length > 1)
    return [`Multiple attackers targeting ${f(Tt[0])}: ${f(T[0])}`, `Multiple attackers targeting ${f(Tt[0])}: ${SPAN}${f(T[0])}</span>`, f(T[0])]
  if (S.length === 1 && T.length > 1)
    return [`${f(St[0])}: ${f(S[0])} attacking multiple targets`, `${f(St[0])}: ${SPAN}${f(S[0])} attacking multiple targets</span>`, f(S[0])]
  if (C.length > 1) {
    const t = 'Multiple targets/suspects possibly compromised or breached'
    return [t, t, null]
  }
  if (C.length === 1) return [`${f(C[0])} is possibly compromised or breached`, `${SPAN}${f(C[0])}</span> is possibly compromised or breached`, f(C[0])]
  if (dn === 1) return [f(D[0]), f(D[0]), null]
  return ['Attack pattern detected', 'Attack pattern detected', null]
}

function subgraph(g: Graph, keys: string[], weightFilter: number): Dict | null {
  let total = 0
  for (const k of keys) total += pyInt(g.get(k)!.attrs.weight)
  if (!(total > weightFilter)) return null

  const inComp = new Set(keys)
  const links: { source: Id; target: Id }[] = []
  for (const k of keys) for (const t of g.get(k)!.succ) if (inComp.has(t)) links.push({ source: g.get(k)!.id, target: g.get(t)!.id })

  const nodes: Dict[] = keys.map((k) => {
    const n = g.get(k)!
    const a = n.attrs
    return a.group === 'signal'
      ? {
          id: n.id, weight: a.weight, group: 'signal', DetectionName: a.dname, DetectionTactic: a.dtactic,
          DetectionTechnique: a.dtechnique, DetectionSubtechnique: a.dsubtechnique, DetectionConfidence: a.dconfidence,
          DetectionSeverity: a.dseverity, CNAMTime: a.timestamp, SourceId: a.sourceid, Version: a.version,
        }
      : { id: n.id, group: a.group, field: a.field }
  })

  // get_meta — node order (D1).
  const D: unknown[] = [], S: Id[] = [], St: unknown[] = [], T: Id[] = [], Tt: unknown[] = [], C: Id[] = []
  for (const k of keys) {
    const n = g.get(k)!
    if (n.attrs.group === 'signal') D.push(n.attrs.dname)
    else if (n.attrs.group === 'suspect') {
      St.push(n.attrs.field)
      S.push(n.id)
    } else if (n.attrs.group === 'target') {
      Tt.push(n.attrs.field)
      T.push(n.id)
    } else C.push(n.id)
  }
  const [name, titleHtml, entityName] = subgraphName(D, S, St, T, Tt, C)
  if (links.length > 1000) return null // F6 — silently

  // Merge (F7, F8, F9).
  const kept: Dict[] = []
  const mergeIds = new Map<unknown, unknown[]>()
  const entities: Dict[] = []
  const init = (i: Dict) => {
    i.count = 1
    i.starttime = i.CNAMTime
    i.endtime = i.CNAMTime
    i.connected_sig_workbooks = new Map([[pyJsonKey(i.SourceId), [i.Version]]])
    delete i.SourceId
    delete i.Version
    kept.push(i)
    mergeIds.set(i.id, [])
  }
  for (const i of nodes) {
    if (i.group !== 'signal') {
      entities.push(i)
      continue
    }
    if (kept.length === 0) {
      init(i)
      continue
    }
    let flag = false
    for (const node of [...kept]) {
      if (!COMP_KEYS.every((key) => pyEq(node[key], i[key]))) continue
      flag = false
      let brk = false
      for (const key of ['source', 'target'] as const) {
        const other = key === 'source' ? 'target' : 'source'
        for (const link of links) {
          if (!pyEq(link[key], node.id)) continue
          for (const link2 of links) {
            if (!pyEq(link2[key], i.id)) continue
            if (pyEq(link[other], link2[other])) flag = true
            else {
              flag = false
              brk = true
              break
            }
          }
          if (brk) break
        }
        if (brk) break
      }
      if (flag) {
        node.count = (node.count as number) + 1
        const wb = node.connected_sig_workbooks as Map<string, unknown[]>
        const versions = wb.get(pyJsonKey(i.SourceId)) ?? []
        if (!versions.some((x) => pyEq(x, i.Version))) versions.push(i.Version)
        wb.set(pyJsonKey(i.SourceId), versions)
        if (pyLess(i.CNAMTime, node.starttime)) node.starttime = i.CNAMTime
        else if (pyLess(node.endtime, i.CNAMTime)) node.endtime = i.CNAMTime
        mergeIds.get(node.id)!.push(i.id)
        kept.splice(kept.indexOf(node), 1)
        kept.push(node)
        break
      }
    }
    if (!flag) init(i)
  }

  let minus = 0
  for (const [id, merged] of mergeIds) {
    minus += merged.length
    for (const l of links) {
      if (merged.some((m) => pyEq(m, l.target))) l.target = id as Id
      if (merged.some((m) => pyEq(m, l.source))) l.source = id as Id
    }
  }
  const seenLink = new Set<string>()
  const dedupedLinks = links.filter((l) => {
    const k = `${keyOf(l.source)}|${keyOf(l.target)}`
    if (seenLink.has(k)) return false
    seenLink.add(k)
    return true
  })
  const outSignals = kept.map((n) => ({
    ...n,
    connected_sig_workbooks: Object.fromEntries(n.connected_sig_workbooks as Map<string, unknown[]>),
  }))
  if (!(outSignals.length > 1)) return null
  return {
    total_weight: total,
    links: dedupedLinks,
    nodes: [...outSignals, ...entities],
    no_of_nodes: keys.length - minus,
    no_of_signals: D.length - minus,
    no_of_suspects: S.length,
    no_of_targets: Tt.length,
    no_of_unique_signals: outSignals.length,
    no_of_compromised: C.length,
    subgraph_name: name,
    title_html: titleHtml,
    entity_name: entityName,
    merge_ids: Object.fromEntries([...mergeIds].map(([id, m]) => [String(id), [...m, id]])),
  }
}

export interface ConnectedSignalsBody {
  status: 'success'
  task_status: 'Task executed successfully'
  execution_time: string
  total_count: 0
  result: []
  graph_data: Dict[]
}

/** Run the worker over `rows`; the result-endpoint body. Throws ConnectedSignalsPortError where the worker raises. */
export function connectedSignals(rows: WorkerRow[], weightFilter: number, now: () => number = () => Date.now()): ConnectedSignalsBody {
  const t0 = now()
  const g = build(rows)
  const graph_data: Dict[] = []
  for (const keys of components(g)) {
    const sg = subgraph(g, keys, weightFilter)
    if (sg) graph_data.push(sg)
  }
  return {
    status: 'success',
    task_status: 'Task executed successfully',
    execution_time: `${((now() - t0) / 1000).toFixed(4)} sec`,
    total_count: 0,
    result: [],
    graph_data,
  }
}
